import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, Loader2 } from "lucide-react";

const RANGE_PRESETS = [
  { key: "all", label: "All time" },
  { key: "this-month", label: "This month" },
  { key: "last-month", label: "Last month" },
  { key: "this-year", label: "This year" },
  { key: "custom", label: "Custom range" },
];

function computeRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n) => String(n).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (preset === "this-month") return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  if (preset === "last-month") return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
  if (preset === "this-year") return { from: `${y}-01-01`, to: `${y}-12-31` };
  return { from: "", to: "" };
}

export function ExportButton({ apiPath, title }) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState("xlsx");
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const pickPreset = (p) => {
    setPreset(p);
    if (p !== "custom") {
      const r = computeRange(p);
      setFrom(r.from);
      setTo(r.to);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ format });
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
      const res = await api.get(`/entities/${apiPath}/export?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `${apiPath}_${stamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${title} as ${format.toUpperCase()}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid={`${apiPath}-export-btn`} className="gap-2">
        <Download className="h-4 w-4" /> Export
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Export {title}</DialogTitle>
            <DialogDescription>Choose a format and date range. Blank range exports all rows.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs mb-2 block">Format</Label>
              <div className="grid grid-cols-3 gap-2">
                {["xlsx", "pdf", "csv"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFormat(f)}
                    data-testid={`export-fmt-${f}`}
                    className={`px-3 py-2 rounded-md border text-sm uppercase tracking-wider transition-colors ${
                      format === f ? "border-brand text-brand bg-brand/10" : "border-input hover:border-muted-foreground"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs mb-2 block">Range</Label>
              <div className="grid grid-cols-2 gap-2">
                {RANGE_PRESETS.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => pickPreset(r.key)}
                    data-testid={`export-range-${r.key}`}
                    className={`px-3 py-2 rounded-md border text-xs text-left transition-colors ${
                      preset === r.key ? "border-brand text-brand bg-brand/10" : "border-input hover:border-muted-foreground"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {preset !== "all" && preset !== "custom" && (from || to) && (
              <p className="text-xs text-muted-foreground">
                Resolved: <span className="font-mono text-foreground">{from || "—"}</span> to <span className="font-mono text-foreground">{to || "—"}</span>
              </p>
            )}

            {preset === "custom" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="export-from-date" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="export-to-date" className="mt-1" />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={run} disabled={busy} data-testid="export-run-btn" className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {busy ? "Building…" : `Download ${format.toUpperCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
